/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Breadcrumbs, Button, Divider, IconButton, InputAdornment, Tooltip } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import api from "../../api";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { StyledBreadCrumbContainer, StyledLink } from "../../components/navigable_breadcrumb_link/styles";
import { IndexTable } from "../../components/index_table";
import { Loader } from "../../components/loader";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { invalidateQueryKeys } from "../../hooks/mutations/utils/invalidateQueries";
import { cityMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/cityMutationKeys";
import { StyledBox, StyledSearchBar } from "../reports/global_employee_search/styles";
import { Query_Keys } from "../../lib/queryKeys";
import { checkAdmin } from "../../utils/checkAdmin";
import { useDebounce } from "../../utils/useDebounce";
import { resetPageAfterDeletion } from "../../utils/resetPageAfterDeletion";
import { StyledCreateButton } from "../restaurant_groups/styles";
import { CityFormDialog } from "./city_form_dialog";
import { CitiesContainer, StyledActionBar, StyledDefaultChip, StyledTableSection, StyledTitle } from "./styles";

const LOAD_ERROR = 'Unable to load cities. Please try again.'

// Sort keys match the cities API's sort_by values.
const COLUMNS = [
  { key: 'id', label: 'Id', sortable: true },
  { key: 'name', label: 'Name', sortable: true },
  { key: 'center', label: 'Center (Lat, Lng)', sortable: false, width: 220 },
  { key: 'bounds', label: 'Bounds (N, S, E, W)', sortable: false, width: 380 },
  { key: 'restaurants_count', label: 'Restaurants', sortable: true },
  { key: 'is_default', label: 'Default', sortable: false },
  { key: 'actions', label: 'Actions', sortable: false },
]

const formatCoordinate = (value) => (value === null || value === undefined ? '-' : String(value))

// Full text in a tooltip, since table cells cut long values short.
const textCell = (text) => <span title={text}>{text}</span>

const toRow = (city, renderActions) => ({
  id: city.id,
  name: textCell(city.name),
  center: textCell(`${formatCoordinate(city.center_lat)}, ${formatCoordinate(city.center_lng)}`),
  bounds: textCell([city.north, city.south, city.east, city.west].map(formatCoordinate).join(', ')),
  restaurants_count: city.restaurants_count ?? 0,
  is_default: city.is_default
    ? <StyledDefaultChip size="small" label="Default" data-testid={`city-default-${city.id}`} />
    : '-',
  actions: renderActions(city),
})

const iconColor = (enabled) => (enabled ? '#707070' : '#e0e0e0')

export const CitiesPage = () => {
  const { admin } = useSelector((state) => state.admin)
  const superRole = checkAdmin(admin?.formatted_roles)
  const canReadCities = Boolean(superRole)
  // Only super admins may change cities; super viewers see the controls disabled.
  const canManageCities = superRole?.role === 'admin'
  const queryClient = useQueryClient()
  // One dialog at a time; `city` is kept on close so the closing dialog doesn't flicker.
  const [dialog, setDialog] = useState({ type: null, city: null })
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState('desc')
  const debouncedSearch = useDebounce(search.trim(), 300)

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: Query_Keys.cities({ page, itemsPerPage, search: debouncedSearch, sortBy, sortOrder }),
    queryFn: async () => {
      const response = await api.get('/v1/admin/cities', {
        params: {
          page,
          per_page: itemsPerPage,
          sort_by: sortBy,
          sort_order: sortOrder,
          search: debouncedSearch || undefined,
        },
      })
      return { data: response?.data || [], pageCount: Number(response?.headers?.['total-count']) || 0 }
    },
    enabled: canReadCities,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
  })

  const closeDialog = () => setDialog((current) => ({ ...current, type: null }))

  const { mutate: deleteCity, isPending: isDeleting } = useOtMutation({
    mutationFn: (city) => api.delete(`/v1/admin/cities/${city.id}`),
    invalidateKeys: cityMutationKeyGroups.delete,
    successMessage: 'City deleted successfully',
    onSuccess: () => {
      closeDialog()
      resetPageAfterDeletion({ totalCount: data?.pageCount, page, itemsPerPage, setPage })
    },
    // The toast shows the server's reason. After a refusal or 404, refresh the list in case it is out of date.
    onError: (error) => {
      closeDialog()
      if ([404, 422].includes(error?.response?.status)) {
        invalidateQueryKeys(queryClient, cityMutationKeyGroups.delete)
      }
    },
  })

  const renderActions = (city) => (
    <div style={{ display: 'flex', gap: '2px' }}>
      <Tooltip title="Edit" arrow>
        <span>
          <IconButton
            onClick={() => setDialog({ type: 'form', city })}
            disabled={!canManageCities}
            data-testid={`city-edit-button-${city.id}`}
          >
            <ModeEditIcon sx={{ color: iconColor(canManageCities) }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <span>
          <IconButton
            onClick={() => setDialog({ type: 'delete', city })}
            disabled={!canManageCities}
            data-testid={`city-delete-button-${city.id}`}
          >
            <DeleteIcon sx={{ color: iconColor(canManageCities) }} />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  )

  const handleSearch = (value) => {
    setSearch(value)
    setPage(1)
  }

  const handleSort = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  const renderList = () => {
    // Full loader on every fetch, except a search shows "Searching..." in the table.
    if (!admin || (canReadCities && isFetching && !debouncedSearch)) {
      return <Loader />
    }

    if (!canReadCities) {
      return <Alert severity="info">Only super admins and super viewers can view cities.</Alert>
    }

    if (isError) {
      return (
        <Alert
          severity="error"
          data-testid="cities-error"
          action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
        >
          {error?.response?.data?.error || LOAD_ERROR}
        </Alert>
      )
    }

    return (
      <IndexTable
        report={false}
        columns={COLUMNS}
        data={data?.data?.map((city) => toRow(city, renderActions))}
        tableId="cities"
        type="entries"
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        page={page}
        setPage={setPage}
        pageCount={data?.pageCount || 0}
        sortMode="server"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSort}
        searchLoading={isFetching && Boolean(debouncedSearch)}
        hasSearchTerm={Boolean(debouncedSearch)}
        emptyStateText={debouncedSearch ? 'No cities match your search' : 'No cities added yet'}
      />
    )
  }

  return (
    <CitiesContainer>
      <StyledBreadCrumbContainer data-testid="breadcrumb-section">
        <Breadcrumbs>
          <NavigableBreadcrumbLink
            LinkComponent={StyledLink}
            label="Home"
            to="/admin/dashboard"
            dataTestId="home-breadcrumb-option"
          />
          <StyledLink color={'#515152'}>Cities</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <StyledBox report={false}>
        <StyledTitle>Cities</StyledTitle>
        {canReadCities && (
          <StyledSearchBar
            value={search}
            placeholder="Search by name or ID"
            InputProps={{
              startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
            }}
            inputProps={{ maxLength: 100, 'data-testid': 'cities-search-input' }}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete="off"
          />
        )}
      </StyledBox>

      <Divider style={{ borderStyle: 'solid', borderWidth: '1px' }} />

      {canReadCities && (
        <StyledActionBar>
          <StyledCreateButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialog({ type: 'form', city: null })}
            disabled={!canManageCities}
            data-testid="city-create-button"
          >
            Create City
          </StyledCreateButton>
        </StyledActionBar>
      )}

      <StyledTableSection>{renderList()}</StyledTableSection>

      <CityFormDialog open={dialog.type === 'form'} city={dialog.city} onClose={closeDialog} />

      <ConfirmationDialog
        open={dialog.type === 'delete'}
        onClose={closeDialog}
        Icon={CloseIcon}
        dialogTitle="Are you sure you want to delete the city?"
        dialogBody={(
          <>
            {'This action will permanently delete the'}
            <span style={{ fontWeight: '600' }}>{` '${dialog.city?.name}' `}</span>
            {'city.'}
          </>
        )}
        confirmationText="Yes, I want to delete the city"
        handleConfirm={() => deleteCity(dialog.city)}
        isLoading={isDeleting}
        dataTestId="city-delete-dialog"
      />
    </CitiesContainer>
  )
}
